import React, { ChangeEvent, FormEvent, MouseEvent, useState } from "react"
import classnames from "classnames"
import parse from "html-react-parser"
import { GraphError } from "javascripts/types"
import { activateAuthNotRequired } from "./api"

import ErrorIcon from "images/icons/icon-error-triangle.svg?raw"

import styles from "./activation.module.scss"

interface TimeZoneOption {
  value: string
  label: string
  iana: string
}

interface Props {
  serviceName: string
  moduleName: string
  liveChannelId?: string
  timeZoneOptions: TimeZoneOption[]
  defaultTimeZone?: string
  successUrl: string
}

// Preselect the reconnect value if present, otherwise the option matching the
// visitor's browser time zone (IANA), otherwise nothing.
const initialTimeZone = (options: TimeZoneOption[], defaultTimeZone?: string) => {
  if (defaultTimeZone) return defaultTimeZone

  const browserIana = Intl.DateTimeFormat().resolvedOptions().timeZone
  return options.find(option => option.iana === browserIana)?.value || ""
}

const ActivateTimezone = ({
  serviceName,
  moduleName,
  liveChannelId,
  timeZoneOptions,
  defaultTimeZone,
  successUrl,
}: Props) => {
  const [timeZone, setTimeZone] = useState<string>(() => initialTimeZone(timeZoneOptions, defaultTimeZone))
  const [error, setError] = useState<string>()
  const [submitDisabled, setSubmitDisabled] = useState<boolean>(false)

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setTimeZone(e.target.value)
    setSubmitDisabled(false)
  }

  const handleSubmit = async (e: FormEvent | MouseEvent) => {
    e.preventDefault()
    setSubmitDisabled(true)
    setError(undefined)

    try {
      const response = await activateAuthNotRequired(moduleName, { time_zone: timeZone }, Number(liveChannelId))
      if (response?.activated) {
        window.location.assign(successUrl)
      } else if (response?.errors?.length) {
        setError(response.errors[0].message)
        setSubmitDisabled(false)
      } else {
        setError(`Error connecting ${serviceName}`)
        setSubmitDisabled(false)
      }
    } catch (e) {
      console.error(e)
      setError((e as GraphError).details || `Error connecting ${serviceName}`)
      setSubmitDisabled(false)
    }
  }

  return (
    <div className={styles.activation}>
      <div className={styles["service-fields"]}>
        <h1 role="heading">
          {liveChannelId ? "Reconnect" : "Connect"} to {serviceName}
        </h1>
        <div className={styles["service-activation-form"]}>
          <form className="new_live_channel" id="new_live_channel" onSubmit={handleSubmit}>
            <div className={styles["live-channel-fields"]}>
              <div className={classnames(styles["activation-field-entry"], { [styles.error]: !!error })}>
                <label htmlFor="time_zone">Time zone</label>
                <select id="time_zone" name="time_zone" value={timeZone} onChange={handleChange} autoFocus>
                  <option value="" disabled>
                    Select a time zone
                  </option>
                  {timeZoneOptions.map(({ value, label }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              {error && (
                <div className={classnames(styles["after-message"], styles.submit)} role="alert">
                  <span>{parse(ErrorIcon)}</span>
                  {error}
                </div>
              )}
              <div className={styles["send-pin"]}>
                <button className="button-secondary" onClick={handleSubmit} disabled={submitDisabled || !timeZone}>
                  {liveChannelId ? "Update" : "Connect"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ActivateTimezone
